import type { FromWorker, ToWorker } from '../shared/protocol.ts';
/**
 * The main thread's end of the worker protocol (UX-FLOW-03). One worker at a time; `restart()` terminates it and starts
 * a fresh one (how Cancel works: analysis is synchronous CPU work inside the engine, so a running stage cannot be
 * interrupted from inside).
 *
 * Trusted Types: the page's CSP is `require-trusted-types-for 'script'; trusted-types provenance-worker`, and
 * `new Worker(url)` is a Trusted Types sink in every engine, so the worker URL goes through the one policy this app
 * creates. It accepts only a same-origin script URL and throws on anything else (SECURITY §7.2).
 */
import workerUrl from '../worker/index.ts?worker&url';
import { spawnWorker } from './trusted-worker.ts';

type Handler = (m: FromWorker) => void;

export class WorkerClient {
	private worker: Worker | null = null;
	private handlers = new Set<Handler>();
	private nextId = 1;
	/** Called when the worker dies (error event or a message that cannot be read). */
	onCrash: (() => void) | null = null;

	/** A fresh id for a request. */
	id(): number {
		return this.nextId++;
	}

	subscribe(h: Handler): () => void {
		this.handlers.add(h);
		return () => this.handlers.delete(h);
	}

	private ensure(): Worker {
		if (this.worker) return this.worker;
		const w = spawnWorker(workerUrl, 'provenance-analysis');
		w.onmessage = (e: MessageEvent<FromWorker>) => {
			for (const h of this.handlers) h(e.data);
		};
		w.onerror = (e) => {
			e.preventDefault();
			console.error(`analysis worker error: ${e.message} (${e.filename}:${e.lineno})`);
			this.crashed(w);
		};
		w.onmessageerror = () => this.crashed(w);
		this.worker = w;
		return w;
	}

	private crashed(w: Worker): void {
		if (this.worker !== w) return;
		this.worker = null;
		w.terminate();
		this.onCrash?.();
	}

	send(m: ToWorker, transfer?: Transferable[]): void {
		this.ensure().postMessage(m, transfer ?? []);
	}

	/** Sends `m` and resolves with the first reply of a type in `types` carrying the same id (or rejects on `error`). */
	request<T extends FromWorker['type']>(
		m: ToWorker,
		types: readonly T[]
	): Promise<Extract<FromWorker, { type: T }>> {
		return new Promise((resolve, reject) => {
			const off = this.subscribe((r) => {
				if (r.id !== m.id) return;
				if ((types as readonly string[]).includes(r.type)) {
					off();
					resolve(r as Extract<FromWorker, { type: T }>);
				} else if (r.type === 'error') {
					off();
					reject(Object.assign(new Error(r.message), { code: r.code }));
				}
			});
			this.send(m);
		});
	}

	/** Terminate the worker; the next `send` starts a new one. Pending requests are never answered. */
	restart(): void {
		this.worker?.terminate();
		this.worker = null;
	}
}

export const client = new WorkerClient();
